/**
 * Pricing tier logic for C-Tribe Festival 2027
 * Handles tiered pricing with presale discounts
 */

import { PricingTier, TicketType } from '@/models/ticketType';

export interface TierConfig {
  start: string;  // ISO date string
  end: string;    // ISO date string
  discount: number; // Percentage off (0-100)
  label: string;  // Display label
}

export const PRICING_TIERS: Record<PricingTier, TierConfig> = {
  presale1: {
    start: '2027-01-05T00:00:00-07:00',
    end: '2027-03-15T00:00:00-06:00',
    discount: 50,
    label: 'Early Bird (50% Off)',
  },
  presale2: {
    start: '2027-03-15T00:00:00-06:00',
    end: '2027-05-15T00:00:00-06:00',
    discount: 30,
    label: 'Presale 2 (30% Off)',
  },
  presale3: {
    start: '2027-05-15T00:00:00-06:00',
    end: '2027-07-15T00:00:00-06:00',
    discount: 20,
    label: 'Presale 3 (20% Off)',
  },
  fullPrice: {
    start: '2027-07-15T00:00:00-06:00',
    end: '2027-08-12T00:00:00-06:00',
    discount: 0,
    label: 'Full Price',
  },
};

/**
 * Get the current pricing tier based on the current date
 * @param date - Optional date to check (defaults to now)
 * @returns The current pricing tier
 */
export function getCurrentPricingTier(date?: Date): PricingTier {
  const now = date || new Date();

  const presale1End = new Date(PRICING_TIERS.presale1.end);
  const presale2End = new Date(PRICING_TIERS.presale2.end);
  const presale3End = new Date(PRICING_TIERS.presale3.end);

  if (now < presale1End) return 'presale1';
  if (now < presale2End) return 'presale2';
  if (now < presale3End) return 'presale3';
  return 'fullPrice';
}

/**
 * Get the Stripe Price ID for a specific tier
 * @param ticketType - The ticket type document
 * @param tier - The pricing tier (optional, defaults to current)
 * @returns The Stripe Price ID
 */
export function getPriceIdForTier(
  ticketType: TicketType,
  tier?: PricingTier
): string {
  const currentTier = tier || getCurrentPricingTier();
  return ticketType.prices[currentTier];
}

/**
 * Get the tier configuration for a specific tier
 * @param tier - The pricing tier (optional, defaults to current)
 * @returns The tier configuration
 */
export function getTierConfig(tier?: PricingTier): TierConfig {
  const currentTier = tier || getCurrentPricingTier();
  return PRICING_TIERS[currentTier];
}

/**
 * Calculate the discounted price in cents
 * @param basePrice - The base price in cents
 * @param tier - The pricing tier (optional, defaults to current)
 * @returns The discounted price in cents
 */
export function calculateDiscountedPrice(
  basePrice: number,
  tier?: PricingTier
): number {
  const config = getTierConfig(tier);
  const discountMultiplier = (100 - config.discount) / 100;
  return Math.round(basePrice * discountMultiplier);
}

/**
 * Format price for display
 * @param priceInCents - Price in cents
 * @param currency - Currency code (default CAD)
 * @returns Formatted price string
 */
export function formatPrice(
  priceInCents: number,
  currency: string = 'CAD'
): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
  }).format(priceInCents / 100);
}

/**
 * Get time remaining until next pricing tier
 * @returns Object with days, hours, minutes remaining or null if in final tier
 */
export function getTimeUntilNextTier(): {
  days: number;
  hours: number;
  minutes: number;
  nextTier: PricingTier | null;
} | null {
  const now = new Date();
  const currentTier = getCurrentPricingTier(now);

  if (currentTier === 'fullPrice') {
    return null;
  }

  const tierEndDate = new Date(PRICING_TIERS[currentTier].end);
  const diff = tierEndDate.getTime() - now.getTime();

  if (diff <= 0) {
    return null;
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  const tierOrder: PricingTier[] = ['presale1', 'presale2', 'presale3', 'fullPrice'];
  const currentIndex = tierOrder.indexOf(currentTier);
  const nextTier = currentIndex < tierOrder.length - 1
    ? tierOrder[currentIndex + 1]
    : null;

  return { days, hours, minutes, nextTier };
}
